import { Link } from "react-router-dom";
import { ArrowRight, Eye, Camera, Shield, FileCheck, Users } from "lucide-react";
import { motion } from "framer-motion";

const features = [
  {
    title: "Existing System Integration",
    description: "Helais Sentinel integrates with your existing cameras, perimeter sensors and site workflows. No requirement to replace working infrastructure.",
    icon: Camera,
  },
  {
    title: "Controlled Aerial Response",
    description: "Qualified aerial observation as an optional response asset. Deploy only when the situation warrants escalation.",
    icon: Shield,
  },
  {
    title: "Operator Authorisation",
    description: "Human operators authorise every aerial deployment. The system supports decisions; it does not replace operator authority.",
    icon: Users,
  },
  {
    title: "Evidence Logging",
    description: "Every observation, alert and response is logged for audit and review. Maintain complete accountability.",
    icon: FileCheck,
  },
];

export default function ProtectiveSecurityPage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/solutions" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Solutions
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <Eye className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Protective Security
            </h1>
            <p className="text-subtitle max-w-2xl mb-4">
              Helais Sentinel for site protection
            </p>
            <p className="text-body max-w-2xl">
              Fixed systems can lose track or leave blind areas. Operators need controlled escalation and an auditable response. Helais Sentinel provides continuity of situational awareness with operator-controlled aerial observation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              The Continuity Problem
            </h2>
            <p className="text-body mb-4">
              Traditional perimeter security relies on fixed cameras and sensors. When a target moves between coverage zones, or into areas without line-of-sight, the picture breaks down.
            </p>
            <p className="text-body">
              Helais Sentinel maintains situational awareness by adding controlled aerial observation to your existing security infrastructure. When an alert triggers, operators can authorise an aerial response to track and observe—without losing the target.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Capabilities</p>
            <h2 className="text-headline text-foreground">Helais Sentinel</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="card-premium p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="h-10 w-10 rounded border border-border bg-muted/50 flex items-center justify-center mb-4">
                  <feature.icon className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="text-label text-foreground mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-headline text-foreground mb-6 text-center">
              Pilot Scope & Dependencies
            </h2>
            <div className="card-pillar p-8">
              <p className="text-body mb-6">
                Every Helais Sentinel deployment is scoped to the specific site, its existing sensor coverage, and the operational rules that govern response. We work with you to define:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Site boundaries and coverage requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Integration points with existing security systems</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Operator authorisation workflows</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Regulatory and airspace considerations</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="h-1.5 w-1.5 rounded-full bg-muted-foreground mt-2 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">Evidence retention and audit requirements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Scope a Sentinel Pilot
            </h2>
            <p className="text-body mb-8">
              Tell us about your site, current sensors, and the operational problem you need to solve. We will scope a pilot that demonstrates value before broader deployment.
            </p>
            <Link to="/contact?type=sentinel" className="btn-primary">
              Discuss Your Site
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
